/**
 * \u6838\u5fc3\u7c7b\u578b\u4e0e\u5e38\u91cf \u2014\u2014 \u4e25\u683c\u5bf9\u9f50 GDD\u3002
 *
 * S1-board-match.md \uff1aBoard / Cell / MatchGroup / Coord
 * S3-special-blocks.md \uff1aSpecialType / SpecialBlock
 *
 * \u7ea6\u5b9a\uff1a\u5750\u6807\u7edf\u4e00\u4e3a { c, r }\uff0cr = 0 \u5728\u9876\u90e8\u3002
 */

// \u2500\u2500\u2500\u2500 S1 \u68cb\u76d8\u6a21\u578b \u2500\u2500\u2500\u2500

export const BOARD_SIZE = 8;
export const COLOR_COUNT = 6;
export const MIN_MATCH_LEN = 3;

/** \u516d\u8272\u4ee3\u53f7\uff1b\u4e0e render/palette.ts \u7684 ColorKey \u4e00\u4e00\u5bf9\u5e94 */
export type PieceColor = 'mercury' | 'venus' | 'earth' | 'mars' | 'saturn' | 'neptune';

export const ALL_COLORS: readonly PieceColor[] = [
  'mercury', 'venus', 'earth', 'mars', 'saturn', 'neptune',
] as const;

export interface Coord { c: number; r: number }

/** S1 \u00a72.1\uff1a\u68cb\u76d8\u7ed3\u6784\u3002cells \u7d22\u5f15\u4e3a [r][c]\uff0cr = 0 \u5728\u9876\u90e8\u3002 */
export interface Board0 {
  size: number;
  cells: Cell[][];
}

/** S3 \u00a72.1\uff1a\u56db\u578b\u7279\u6b8a\u5757 */
export type SpecialType = 'THRUSTER' | 'METEOR_BURST' | 'BLACK_HOLE' | 'NEBULA_CORE';

export type CellKind = 'PIECE' | 'SPECIAL' | 'OBSTACLE' | 'EMPTY';

export interface Cell {
  kind: CellKind;
  color?: PieceColor;
  special?: SpecialType;
  /** \u969c\u788d HP\uff08\u672c\u5207\u7247\u672a\u542f\u7528\uff0c\u9884\u7559\uff09 */
  hp?: number;
}

/** S1 \u00a72.2\uff1a\u5339\u914d\u7ec4 */
export interface MatchGroup {
  cells: Coord[];
  /** \u5f62\u72b6\u63d0\u793a\uff08\u53ef\u591a\u4e2a\uff09 */
  shapeHints: ShapeType[];
  maxLineLength: number;
  color: PieceColor;
}

export type ShapeType = 'LINE_3' | 'LINE_4' | 'LINE_5+' | 'LT' | 'SQUARE';

/** S3 \u00a72.5\uff1a\u5f62\u72b6\u4f18\u5148\u7ea7\u88c1\u51b3 */
export const SHAPE_PRIORITY: readonly ShapeType[] = [
  'SQUARE', 'LINE_5+', 'LT', 'LINE_4', 'LINE_3',
] as const;

/** \u4f18\u5148\u7ea7 \u2192 \u7279\u6b8a\u5757\u7c7b\u578b\u6620\u5c04 */
export const SHAPE_TO_SPECIAL: Partial<Record<ShapeType, SpecialType>> = {
  'LINE_4': 'THRUSTER',
  'LT': 'METEOR_BURST',
  'LINE_5+': 'BLACK_HOLE',
  'SQUARE': 'NEBULA_CORE',
};

// \u2500\u2500\u2500\u2500 \u5750\u6807\u5de5\u5177 \u2500\u2500\u2500\u2500

export function coordEq(a: Coord, b: Coord): boolean {
  return a.c === b.c && a.r === b.r;
}

export function coordKey(c: Coord): string {
  return `${c.c},${c.r}`;
}

export function inBoard(c: Coord): boolean {
  return c.c >= 0 && c.c < BOARD_SIZE && c.r >= 0 && c.r < BOARD_SIZE;
}

/** \u56db\u5411\u90bb\u63a5\uff08\u4e0d\u542b\u5bf9\u89d2\uff09 */
export function neighbors4(c: Coord): Coord[] {
  return [
    { c: c.c - 1, r: c.r },
    { c: c.c + 1, r: c.r },
    { c: c.c, r: c.r - 1 },
    { c: c.c, r: c.r + 1 },
  ].filter(inBoard);
}

/** S8 \u00a74\uff1a\u56db\u5411\u65b9\u5411\u5224\u5b9a */
export type Direction = 'up' | 'down' | 'left' | 'right';

export function directionTo(from: Coord, to: Coord): Direction | null {
  const dc = to.c - from.c;
  const dr = to.r - from.r;
  if (Math.abs(dc) + Math.abs(dr) !== 1) return null;
  if (dc === 1) return 'right';
  if (dc === -1) return 'left';
  if (dr === 1) return 'down';
  return 'up';
}
